package mapsearch.historyPathTool

import analyticsdashboard.AnalyticsTimeFilterStore
import analyticsdashboard.TimeFilter
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.pointCoordinates
import apiclient.search.ObjectSearchResult
import auth.ApiUserStore
import auth.FeatureFlagStore
import auth.Features
import com.jillesvangurp.geojson.Geometry
import com.jillesvangurp.geojson.LineStringCoordinates
import data.objects.ActiveObjectStore
import dev.fritz2.core.RootStore
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.datetime.Instant
import map.MapLayersStore
import maplibreGL.FormationMap
import maplibreGL.LngLat
import maplibreGL.MaplibreGeoJSON
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.Marker
import maplibreGL.renderer.ADDITIONAL_GEOMETRY
import maplibreGL.toJs
import mapsearch.MapRenderOverrides
import mapsearch.MarkerGenerators.makeSvgMarkerForMap
import mapsearch.elementId
import mapsearch.historyMarkerEventListener
import search.getSvgIconOptions
import svgmarker.MarkerSize
import theme.FormationColors
import utils.formatDateTime
import utils.parseInstant

class DisplayedPathObjectResultsStore : RootStore<Map<String, ObjectAndResults>>(
    initialData = emptyMap(),
    job = Job(),
) {

    val formationMap: FormationMap by koinCtx.inject()
    val mapRenderOverrides: MapRenderOverrides by koinCtx.inject()
    val apiUserStore: ApiUserStore by koinCtx.inject()
    val activeObjectStore: ActiveObjectStore by koinCtx.inject()
    val mapLayersStore: MapLayersStore by koinCtx.inject()
    val featureFlagStore: FeatureFlagStore by koinCtx.inject()
    private val analyticsTimeFilterStore: AnalyticsTimeFilterStore by koinCtx.inject()

    private val currentRenderedHistoryMarkers = mutableMapOf<String, List<Marker>>()
    private val currentRenderedGeoJSONHistoryPaths = mutableMapOf<String, MaplibreGeoJSON>()

    fun reset() {
        removeHistoriesFromMap(current.keys)
    }

    fun redraw() {
        current.applyFilters(analyticsTimeFilterStore.getCurrent()).also { filteredResults ->
            renderHistoryResultsAndPaths(filteredResults, forceRefreshAll = true)
        }
    }

    val displayOrRemoveTrackedObjectHistory = handle<Pair<String, ObjectAndResults>> { current, (id, data) ->
        if (id in current) {
//            formationMap.removeActiveObjectOverride(id)
            (current - id).applyFilters(analyticsTimeFilterStore.getCurrent()).also { filteredResults ->
//                filteredResults.renderHistoryResultsOnMap()
                renderHistoryResultsAndPaths(filteredResults)
            }
        } else {
            // filter results and display on map
            current.add(data).applyFilters(analyticsTimeFilterStore.getCurrent()).also { filteredResults ->
//                filteredResults.renderHistoryResultsOnMap()
                renderHistoryResultsAndPaths(filteredResults)
            }
        }
    }


    /**
     * updates this store [DisplayedPathObjectResultsStore]
     * with the newly fetched data of any displayed object
     */
    fun applyUpdatesToDisplayedResults(newData: Map<String, ObjectAndResults>) {
        if (mapRenderOverrides.pathToolActive.current) {
            current.mapNotNull { (objId, objectAndHistoryResults) ->
                if (objectAndHistoryResults.geoObject.objectType in historyPathObjectTypeCriteria
                    && featureFlagStore.current[Features.AllowHistoryPaths] == true
                ) {
                    newData[objId]?.let { objId to it } ?: Pair(objId, objectAndHistoryResults)
                } else {
                    null
                }
            }.toMap().also { filteredResults ->
                console.log(
                    "Render updated and filtered results from cache!",
                    filteredResults.map { it.value.geoObject.title to it.value.results.size }.toString(),
                )
                update(filteredResults)
                addChangesToRenderedHistoryResultsAndPaths(filteredResults)
            }
        }
    }

//    fun renderUpdatedResults(results: Map<String, ObjectAndResults>) {
//        current.mapNotNull { (objId, objectAndHistoryResults) ->
//            if (objectAndHistoryResults.geoObject.objectType in historyPathObjectTypeCriteria
//                && featureFlagStore.current[Features.AllowHistoryPaths] == true
//            ) {
//                results[objId]?.let { Pair(objId, it) } ?: Pair(objId, objectAndHistoryResults)
//            } else {
//                null
//            }
//        }.toMap().also { filteredResults ->
//            update(filteredResults)
//            renderHistoryResultsAndPaths(filteredResults)
//        }
//    }

    private fun addChangesToRenderedHistoryResultsAndPaths(
        changedResults: Map<String, ObjectAndResults>
    ) {
        renderHistoryResultsAndPaths(changedResults, forceRefreshIds = changedResults.keys)
    }

    private fun renderHistoryResultsAndPaths(
        results: Map<String, ObjectAndResults>,
        forceRefreshIds: Set<String> = emptySet(),
        forceRefreshAll: Boolean = false
    ) {
        withKoin {
            val objectHistoryResultsCache: ObjectHistoryResultsCache = get()
            val formationMap: FormationMap = get()
            val map = formationMap.map

//            mapRenderOverrides.addPathToolHighlightOverrides(
//                results.map { (_, data) -> data.results.map { it.id } }.flatten().toSet(),
//            )

            if (forceRefreshAll) {
                removeHistoriesFromMap(results.keys)
            } else if (forceRefreshIds.isNotEmpty()) {
                removeHistoriesFromMap((currentRenderedHistoryMarkers.keys - results.keys) + forceRefreshIds)
            } else {
                removeHistoriesFromMap(currentRenderedHistoryMarkers.keys - results.keys)
            }

            val newHistories = if (forceRefreshAll) {
                results
            } else if (forceRefreshIds.isNotEmpty()) {
                results.filter { (id, _) ->
                    id !in currentRenderedHistoryMarkers.keys || id in forceRefreshIds
                }
            } else {
                results.filter { (id, _) ->
                    id !in currentRenderedHistoryMarkers.keys
                }
            }

            newHistories.map { (id, data) ->
                // HistoryEntryMarkers
                currentRenderedHistoryMarkers[id] = data.results.mapNotNull { historyObj ->
                    createHistoryEntryMarker(historyObj)?.let { historyEntryMarker ->
                        historyEntryMarker.addTo(map)
                        historyEntryMarker
                    }
                }
                // HistoryPaths
                // add location of object as last point of the path,
                // to connect the history events to the object
                // (but only if time filter has not filtered out the actual latest event)
                val unfilteredResults = objectHistoryResultsCache.current[id]?.results
                val points = if (!unfilteredResults.isNullOrEmpty()
                    && data.results.contains(
                        unfilteredResults.maxBy {
                            // check here if most recent update is currently rendered or not
                            Instant.parse(it.updatedAt)
                        },
                    )
                ) {
                    (data.results.reversed() + data.geoObject).reversed()
                } else data.results.reversed()
                currentRenderedGeoJSONHistoryPaths[id] = createHistoryPathGeometry(id, points)
            }.also {
                formationMap.geoJSONAdd(
                    currentRenderedGeoJSONHistoryPaths.values.map { it.feature },
                    ADDITIONAL_GEOMETRY,
                    replaceExisting = true,
                )
            }
        }
    }

    /**
     * Removes an objects history completely from the map, historyEntryMarkers and historyPathGeometry
     */
    private fun removeHistoryFromMap(objId: String) {
        currentRenderedHistoryMarkers.remove(objId)?.forEach { marker ->
            marker.remove()
        }
        removeHistoryPathGeoJSONGeometryFromMap(objId)
    }

    private fun removeHistoriesFromMap(objIds: Set<String>) {
        objIds.forEach { objId ->
            removeHistoryFromMap(objId)
        }
    }

    /**
     * Removes the history path geometry from the map
     */
    private fun removeHistoryPathGeoJSONGeometryFromMap(objId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            currentRenderedGeoJSONHistoryPaths.remove(objId)?.let { pathGeometry ->
                formationMap.geoJSONRemove(
                    listOf(pathGeometry.id).toTypedArray(),
                    ADDITIONAL_GEOMETRY,
                )
            }
        }
    }


    private fun createHistoryEntryMarker(historyObj: GeoObjectDetails): Marker? {
        withKoin {
            val activeHistoryEntryStore: ActiveHistoryEntryStore = get()
            val activeEntryId = activeHistoryEntryStore.current?.id
            console.log("ActiveHistoryEntryStoreValue when render ->", activeHistoryEntryStore.current?.title, activeEntryId)
            val isActiveHistoryEntry =
                historyObj.id == activeEntryId
//                historyObj.id == get<MapRenderOverrides>().activeHistoryEntryMarker.current?.id

            return getSvgIconOptions(obj = historyObj)?.let { svgIconOptions ->
                val markerOptions = MaplibreMarkerOptions(
                    element = makeSvgMarkerForMap(
                        id = historyObj.id,
                        title = "${Instant.parse(historyObj.createdAt).formatDateTime()} - ${historyObj.title}",
                        svgIconOptions = svgIconOptions.copy(size = MarkerSize.XXS),
                        objectType = ObjectType.HistoryEntry,
                        active = isActiveHistoryEntry,
                        showTitle = isActiveHistoryEntry,
                        smallTitle = true,
                    ).also { element ->
                        element.addEventListener("click", historyMarkerEventListener)
                    },
                )
                val newMarker = Marker(markerOptions.toJs())
                newMarker.setLngLat(LngLat(historyObj.latLon.lon, historyObj.latLon.lat))
                newMarker
            }
        }
    }

    private fun createHistoryPathGeometry(objId: String, events: List<GeoObjectDetails>): MaplibreGeoJSON {
        val coordinates: LineStringCoordinates = events.map {
            it.latLon.pointCoordinates()
        }.toTypedArray()
        val geometry: Geometry = Geometry.LineString(coordinates)

        return MaplibreGeoJSON(
            type = ObjectType.HistoryEntry,
            id = "${objId}-history-path",
            geometry = geometry,
            title = null,
            lineType = "gradient_green_red",
            lineColor = FormationColors.MarkerYou.color,
            lineWidth = 5.0,
            lineOpacity = 1.0,
        )
    }

    fun reRenderSingleHistoryEventMarker(objectId: String, historyEntryId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map

            currentRenderedHistoryMarkers[objectId] = currentRenderedHistoryMarkers[objectId]?.filter { marker ->
                if (marker.elementId == historyEntryId) {
                    marker.remove()
                    false
                } else true
            }.orEmpty()

            current[objectId]?.results?.firstOrNull { historyEntryObj ->
                historyEntryObj.id == historyEntryId
            }?.let { historyObj ->
                createHistoryEntryMarker(historyObj)?.let { marker ->
                    currentRenderedHistoryMarkers[objectId] = currentRenderedHistoryMarkers[objectId].orEmpty() + marker
                    marker.addTo(map)
                }
            }
        }
    }
}

/**
 * Helper function to add an entry Pair<[GeoObjectDetails], List<[ObjectSearchResult]>
 * to a Map<[String], [ObjectAndResults]>
 */
fun Map<String, ObjectAndResults>.add(
    data: ObjectAndResults
): Map<String, ObjectAndResults> {
    return data.let { (obj, data) ->
        val mutable = this.toMutableMap()
        mutable[obj.id] = ObjectAndResults(obj, data)
        mutable
    }
}

/**
 * Applies pre-defined type filters and time filter to the Tracked Object history events
 */

fun Map<String, ObjectAndResults>.applyFilters(
    timeFilter: TimeFilter
): Map<String, ObjectAndResults> {
    return this.map { entry ->
        entry.toPair().applyFilters(timeFilter)
    }.toMap()
}

fun Pair<String, ObjectAndResults>.applyFilters(
    timeFilter: TimeFilter
): Pair<String, ObjectAndResults> {
    return this.let { (id, data) ->
        id to ObjectAndResults(
            geoObject = data.geoObject,
            results = data.results.filter { result ->
                // HistoryChangeTypeFilter
                result.hasLocation() && result.changeType() in data.geoObject.objectType.historyEntryTypes()
                    // TimeFilter
                    && result.createdAt.parseInstant()?.toEpochMilliseconds()?.let { eventTime ->
                    val start = timeFilter.filterStartDate?.parseInstant()?.toEpochMilliseconds()
                    val end = timeFilter.filterEndDate?.parseInstant()?.toEpochMilliseconds()
                    end?.let {
                        eventTime <= end && start?.let { eventTime >= start } ?: true
                    } ?: start?.let { eventTime >= start } ?: true
                } ?: true
            },
        )
    }
}
